import {
  ChevronRight,
  Mail,
  ExternalLink,
  Menu,
  Heart,
  LockKeyholeOpen,
  ChartNoAxesCombined,
  BookOpen,
  Component,
  MoreHorizontal,
  Cross,
  Home,
  Upload,
  Database,
  Settings,
  BarChart3,
  ArrowLeft,
  Building2,
  Sparkles,
  Search,
  Plus,
  FileText,
  Filter,
} from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useSidebarCollections } from "@/hooks/useSidebarCollections";
import { CollectionsTree } from "./CollectionsTree";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import GradientHeart from "../GradientHeart";
import { CreateBoardInline } from "../boards/CreateBoardInline";
import { useCollections } from "@/hooks/useCollections";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
// ✅ NEW: imports for entitlement  pricing modal
import { useEntitlements } from "@/entitlements/useEntitlements";
import { usePricingModal } from "@/context/PricingModalContext";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { logger } from "@/lib/logger";

// assets
const OBYECT_LOGO_FULL = "/lovable-uploads/obyect_new.svg"; // Use white logo
const OBYECT_LOGO_COLLAPSED = "/lovable-uploads/obyect_new.svg";
const SUBYECT_LOGO = "/lovable-uploads/2386a5c1-03a8-43e9-ab35-e0ece2ebe84a.png";

const mainMenu = [
  { title: "Library", url: "/library", icon: BookOpen },
  { title: "Brands", url: "/brands", icon: Building2 },
  // { title: "Black Friday Board", url: "/black-friday", icon: LockKeyholeOpen },
  { title: "Top picks", url: "/top-picks", icon: ChartNoAxesCombined },
  // { title: "New Years Board", url: "/new-years", icon: Sparkles },
];

const adminMenu = [
  { title: "Overview", url: "/admin", icon: Home },
  { title: "Upload", url: "/admin?view=upload", icon: Upload },
  { title: "Scrape", url: "/admin?view=scrape", icon: Search },
  { title: "Auto-Published", url: "/admin?view=auto-published", icon: Sparkles },
  { title: "Filtered Ads", url: "/admin?view=filtered-ads", icon: Filter },
  { title: "Settings", url: "/admin?view=settings", icon: Settings },
  { title: "Brands", url: "/admin?view=brands", icon: Building2 },
  { title: "Entities", url: "/admin?view=entities", icon: Settings },
  { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
];

// Desktop/iPad tile
function Tile({ to, title, Icon, collapsed }: { to: string; title: string; Icon: any; collapsed: boolean }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={title}>
        <NavLink
          to={to}
          className={({ isActive }) =>
            cn(
              "group block rounded-[12px] px-2 py-1.5 transition-all duration-200",
              "hover:bg-white/5",
              isActive
                ? "bg-white/10 ring-1 ring-white/20 shadow-[0_0_20px_-12px_rgba(255,255,255,0.5)]"
                : "text-white/60 hover:text-white"
            )
          }
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-transparent group-hover:bg-white/5 transition-colors">
              <Icon className="h-4 w-4 text-white/70 group-hover:text-white" />
            </div>
            {!collapsed && <span className="text-[15px] font-medium text-white/90">{title}</span>}
          </div>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// NEW: collapsed icon-only button (high contrast, no tile)
function IconOnly({ to, title, Icon }: { to: string; title: string; Icon: any }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={title}>
        <NavLink
          to={to}
          className={({ isActive }) =>
            cn(
              "flex items-center justify-center rounded-lg p-2 transition-colors",
              "hover:bg-white/10",
              isActive && "bg-white/10"
            )
          }
        >
          <Icon className="h-5 w-5 text-white/80 group-hover:text-white" />
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

type UserInfo = { name: string; email: string; avatar?: string | null } | null | undefined;
// undefined = loading, null = guest, object = logged-in user

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on admin routes
  const isAdminRoute = location.pathname.startsWith("/admin");
  const currentMenu = isAdminRoute ? adminMenu : mainMenu;

  // If user clicks a link for the page they're already on, force a "fresh" visit
  const handleNavClick = (url: string) => (e: React.MouseEvent) => {
    if (location.pathname === url) {
      e.preventDefault();

      navigate(url, {
        replace: true,
        state: {
          resetFilters: true,
          ts: Date.now(), // ensures effect runs even if you click twice
        },
      });
    }
  };

  // ✅ NEW: rename / delete modal state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeCollection, setActiveCollection] = useState<any | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // ✅ NEW: entitlement + pricing modal hooks
  const { isAuthenticated, isSubscribed, entitlementLoaded } = useEntitlements();
  const { openPricingModal } = usePricingModal();

  // For listing in sidebar - only fetch when authenticated
  const { collections, loading, refetch } = useSidebarCollections(isAuthenticated);

  // For creating boards (same helper as BookmarkMenu)
  const { createCollection } = useCollections({
    enabled: true,
    realtime: false, // or true if you want, but not required
  });

  const [collectionsOpen, setCollectionsOpen] = useState(true);
  const [requestsOpen, setRequestsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Check if user has active request subscription
  const [hasRequestSubscription, setHasRequestSubscription] = useState(false);
  useEffect(() => {
    const checkRequestSubscription = async () => {
      if (!isAuthenticated) return;
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;
      
      const { data } = await (supabase.rpc as any)('get_request_quota', {
        p_user_id: user.user.id,
      });
      setHasRequestSubscription(data?.[0]?.has_active_subscription || false);
    };
    checkRequestSubscription();
  }, [isAuthenticated]);
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState(""); 

  // 👉 Local copy that we control (optimistic updates)
  const [sidebarCollections, setSidebarCollections] = useState<any[]>([]);

  // Sync from hook whenever it changes (first load, realtime, etc.)
  useEffect(() => {
    setSidebarCollections(collections || []);
  }, [collections]);

  const [userInfo, setUserInfo] = useState<UserInfo>(undefined); // start "loading"
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const { data } = await supabase.auth.getUser();
      const u = data?.user;
      if (!mounted) return;
      if (!u) return setUserInfo(null);
      const m: any = u.user_metadata ?? {};
      const name = m.full_name || m.name || m.display_name || u.email?.split("@")[0] || "Profile";
      const avatar = m.avatar_url || m.picture || m.image || null;
      setUserInfo({ name, email: u.email ?? "", avatar });
    };
    init();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      const u = s?.user;
      if (!u) return setUserInfo(null);
      const m: any = u.user_metadata ?? {};
      const name = m.full_name || m.name || m.display_name || u.email?.split("@")[0] || "Profile";
      const avatar = m.avatar_url || m.picture || m.image || null;
      setUserInfo({ name, email: u.email ?? "", avatar });
    });
    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleCreateSidebarCollection = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (!isAuthenticated) {
      window.location.href = "/auth/signin";
      return;
    }

    try {
      const created = await createCollection(trimmed, null);
      if (created?.id) {
        setSidebarCollections((prev) => [...prev, created]);
      }
      await refetch?.();
    } catch (e) {
      logger.error("Failed to create sidebar collection", e);
    } finally {
      setShowInlineCreate(false);
    }
  };

  // ✅ OPEN rename dialog
  const openRenameDialog = (collection: any) => {
    setActiveCollection(collection);
    setRenameValue(collection?.name || "");
    setRenameDialogOpen(true);
  };

  // ✅ CONFIRM rename from dialog
  const handleConfirmRename = async () => {
    const trimmed = renameValue.trim();
    if (!activeCollection || !trimmed || trimmed === activeCollection.name) {
      setRenameDialogOpen(false);
      return;
    }

    try {
      await supabase
        .from("user_collections" as any)
        .update({ name: trimmed })
        .eq("id", activeCollection.id);

      // optimistic update
      setSidebarCollections((prev) =>
        prev.map((c) => (c.id === activeCollection.id ? { ...c, name: trimmed } : c))
      );

      await refetch?.();
    } catch (e) {
      logger.error("Failed to rename collection", e);
    } finally {
      setRenameDialogOpen(false);
      setActiveCollection(null);
    }
  };

  // ✅ OPEN delete dialog
  const openDeleteDialog = (collection: any) => {
    setActiveCollection(collection);
    setDeleteDialogOpen(true);
  };

  // ✅ CONFIRM delete from dialog
  const handleConfirmDelete = async () => {
    if (!activeCollection) {
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await supabase
        .from("user_collections" as any)
        .delete()
        .eq("id", activeCollection.id);

      // optimistic update
      setSidebarCollections((prev) => prev.filter((c) => c.id !== activeCollection.id));

      await refetch?.();
    } catch (e) {
      logger.error("Failed to delete collection", e);
    } finally {
      setDeleteDialogOpen(false);
      setActiveCollection(null);
    }
  };




  const handleRenameCollection = async (collection: any) => {
    const currentName = collection.name || "";
    const nextName = window.prompt("Rename collection", currentName);
    if (!nextName) return;

    const trimmed = nextName.trim();
    if (!trimmed || trimmed === currentName) return;

    try {
      const { error } = await supabase
        .from("user_collections" as any)
        .update({ name: trimmed })
        .eq("id", collection.id);

      if (error) {
        logger.error("Failed to rename collection", error);
        return;
      }

      // ✅ update local sidebar state
      setSidebarCollections((prev) =>
        prev.map((c) =>
          c.id === collection.id ? { ...c, name: trimmed } : c
        )
      );

      await refetch?.(); // optional safety
    } catch (e) {
      logger.error("Failed to rename collection", e);
    }
  };

  const handleDeleteCollection = async (collection: any) => {
    const ok = window.confirm(
      `Delete collection "${collection.name}"?\nSaved ads will remain available in the Library.`
    );
    if (!ok) return;

    try {
      const { error } = await supabase
        .from("user_collections" as any)
        .delete()
        .eq("id", collection.id);

      if (error) {
        logger.error("Failed to delete collection", error);
        return;
      }

      // ✅ remove from local sidebar state
      setSidebarCollections((prev) =>
        prev.filter((c) => c.id !== collection.id)
      );

      await refetch?.(); // optional
    } catch (e) {
      logger.error("Failed to delete collection", e);
    }
  };



  const visibleCollections = useMemo(() => {
    const isFavorites = (c: any) => {
      const name = (c?.name || c?.title || "").toString().toLowerCase().trim();
      const slug = (c?.slug || "").toString().toLowerCase().trim();
      return (
        name === "favorites" ||
        slug === "favorites" ||
        c?.type === "favorites" ||
        c?.isFavorite === true ||
        c?.id === "favorites" ||
        c?.key === "favorites"
      );
    };
    return (sidebarCollections || []).filter((c: any) => !isFavorites(c));
  }, [sidebarCollections]);

  const filteredCollections = useMemo(() => {
    if (!searchTerm.trim()) {
      return visibleCollections;
    }
    const search = searchTerm.toLowerCase().trim();
    return visibleCollections.filter((c: any) => {
      const name = (c?.name || "").toLowerCase();
      return name.includes(search);
    });
  }, [visibleCollections, searchTerm]);


  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-black/10 h-full flex flex-col bg-transparent"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* full-height layout wrapper */}
      <div
        className="h-full flex flex-col"
        style={{
          minHeight: "100vh",
          background: 'transparent',
        }}
      >
        <SidebarHeader className="px-2 py-2 border-b border-white/10">
          <div className="flex items-center p-2.5 justify-between">
            <Link to="/library" className="flex items-center gap-2.5 hover-lift" aria-label="Go to library">
              <img src="/lovable-uploads/obyect_white_new.png" alt="Obyect logo" className="h-6 sm:h-7 w-auto" />
            </Link>
            <SidebarTrigger className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10" />
          </div>
        </SidebarHeader>

        <SidebarContent className="flex-1 overflow-y-auto">
          {/* MAIN MENU */}
          <div className={cn(
            "bg-[#13171c]/80 backdrop-blur-xl m-2 rounded-2xl shadow-lg border border-white/5 px-3 py-[10px] transition-all duration-200",
            "group-data-[collapsible=icon]:m-0 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-2 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:backdrop-blur-none group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:rounded-none"
          )}>
            <SidebarGroup>
              <SidebarGroupContent>
                {/* Back to Library link when on admin */}
                {isAdminRoute && (
                  <div className="mb-3 pb-3 border-b border-white/10 group-data-[collapsible=icon]:mb-0 group-data-[collapsible=icon]:pb-0 group-data-[collapsible=icon]:border-b-0">
                    <NavLink
                      to="/library"
                      className="flex items-center gap-2 px-1 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group-data-[collapsible=icon]:justify-center"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span className="group-data-[collapsible=icon]:hidden">Back to Library</span>
                    </NavLink>
                  </div>
                )}
                <SidebarMenu className="space-y-3">
                  {currentMenu.map(({ title, url, icon: Icon }) => {
                    // For admin menu items with query params, check active state manually
                    const isAdminItem = url.startsWith("/admin");
                    const urlPath = url.split("?")[0];
                    const urlView = url.includes("view=") ? url.split("view=")[1] : null;
                    const currentView = new URLSearchParams(location.search).get("view");
                    const isActiveAdmin = isAdminItem && (
                      (url === "/admin" && !currentView && location.pathname === "/admin") ||
                      (urlView && currentView === urlView)
                    );

                    return (
                    <SidebarMenuItem key={title}>
                      <SidebarMenuButton asChild tooltip={title}>
                        <NavLink
                          to={url}
                          end={url === "/" || url === "/admin"}
                          onClick={handleNavClick(url)}
                          className={cn(
                            "group/item flex items-center gap-3 px-3 py-2 rounded-[12px] transition-all duration-200 font-medium",
                            "[[data-collapsible=icon]_&]:justify-center [[data-collapsible=icon]_&]:px-2",
                            isActiveAdmin 
                              ? "bg-[#70BAFF]/15 text-[#70BAFF] shadow-[0_0_20px_-5px_rgba(112,186,255,0.5)] ring-1 ring-[#70BAFF]/30" 
                              : "text-white/60 hover:text-white hover:bg-white/5"
                          )}
                        >

                          {({ isActive: routerActive }) => {
                            const isActive = isAdminItem ? isActiveAdmin : routerActive;
                            return (
                              <>
                                <div
                                    className={cn(
                                      "flex h-6 w-6 items-center justify-center rounded-md transition-all duration-200",
                                      isActive ? "bg-[#70BAFF]/20" : "bg-transparent group-hover/item:bg-white/5"
                                    )}
                                  >
                                    <Icon
                                      className={cn(
                                        "h-4 w-4 transition-colors duration-150",
                                        isActive ? "text-[#70BAFF]" : "text-white/70 group-hover/item:text-white"
                                      )}
                                    />
                                  </div>
                                  <span className={cn(
                                    "text-[15px] [[data-collapsible=icon]_&]:hidden",
                                    isActive ? "text-[#70BAFF]" : "text-white/90"
                                  )}>{title}</span>
                              </>
                            );
                          }}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                </SidebarMenu>

                {/* AD REQUESTS (Collapsible with Brands sub-item) - Only if not admin route */}
                {!isAdminRoute && (
                  <div className="mt-3">
                    <Collapsible open={requestsOpen} onOpenChange={setRequestsOpen}>
                      <div className="flex items-center w-full">
                        <NavLink
                          to="/requests"
                          onClick={handleNavClick("/requests")}
                          className={cn(
                            "group/item flex items-center gap-3 px-3 py-2 rounded-[12px] transition-all duration-200 font-medium flex-1",
                            "[[data-collapsible=icon]_&]:justify-center [[data-collapsible=icon]_&]:px-2",
                            "bg-[#70BAFF]/15 text-[#70BAFF]",
                            "[[data-collapsible=icon]_&]:bg-[#70BAFF]/15 [[data-collapsible=icon]_&]:shadow-none [[data-collapsible=icon]_&]:ring-0",
                            "group-data-[collapsible=expanded]:shadow-[0_0_20px_-5px_rgba(112,186,255,0.5)] group-data-[collapsible=expanded]:ring-1 group-data-[collapsible=expanded]:ring-[#70BAFF]/30"
                          )}
                        >
                          <div className="flex h-6 w-6 items-center justify-center rounded-md transition-all duration-200 bg-[#70BAFF]/20">
                            <Sparkles className="h-4 w-4 text-[#70BAFF]" />
                          </div>
                          <span className="text-[15px] flex-1 text-[#70BAFF] [[data-collapsible=icon]_&]:hidden">Design Requests</span>
                        </NavLink>
                        {hasRequestSubscription && !collapsed && (
                          <CollapsibleTrigger asChild>
                            <button 
                              className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors mr-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 text-white/60 transition-transform',
                                  requestsOpen && 'rotate-90'
                                )}
                              />
                            </button>
                          </CollapsibleTrigger>
                        )}
                      </div>

                      {hasRequestSubscription && (
                        <CollapsibleContent className="mt-1">
                          <div className="pl-9">
                            <NavLink
                              to="/settings/brands"
                              className={({ isActive }) => cn(
                                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors hover:bg-white/5",
                                isActive ? "text-[#70BAFF] bg-[#70BAFF]/10" : "text-white/70 hover:text-white"
                              )}
                            >
                              <Building2 className="h-3.5 w-3.5" />
                              <span>Brands</span>
                            </NavLink>
                          </div>
                        </CollapsibleContent>
                      )}
                    </Collapsible>
                  </div>
                )}

                {/* BRIEFING GENERATOR - Only if not admin route */}
                {/* Temporarily disabled on main branch
                {!isAdminRoute && (
                  <div className="mt-3">
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild tooltip="Briefing Generator">
                        <NavLink
                          to="/briefings"
                          onClick={handleNavClick("/briefings")}
                          className={({ isActive }) => cn(
                            "group/item flex items-center gap-3 px-3 py-2 rounded-[12px] transition-all duration-200 font-medium",
                            "[[data-collapsible=icon]_&]:justify-center [[data-collapsible=icon]_&]:px-2",
                            isActive 
                              ? "bg-[#70BAFF]/15 text-[#70BAFF] shadow-[0_0_20px_-5px_rgba(112,186,255,0.5)] ring-1 ring-[#70BAFF]/30" 
                              : "text-white/60 hover:text-white hover:bg-white/5"
                          )}
                        >
                          {({ isActive }) => (
                            <>
                              <div
                                className={cn(
                                  "flex h-6 w-6 items-center justify-center rounded-md transition-all duration-200",
                                  isActive ? "bg-[#70BAFF]/20" : "bg-transparent group-hover/item:bg-white/5"
                                )}
                              >
                                <FileText
                                  className={cn(
                                    "h-4 w-4 transition-colors duration-150",
                                    isActive ? "text-[#70BAFF]" : "text-white/70 group-hover/item:text-white"
                                  )}
                                />
                              </div>
                              <span className={cn(
                                "text-[15px] [[data-collapsible=icon]_&]:hidden",
                                isActive ? "text-[#70BAFF]" : "text-white/90"
                              )}>Briefing Generator</span>
                            </>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </div>
                )}
                */}

                
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          {/* SECTION TITLE - Hide on admin routes and when collapsed */}
          {!isAdminRoute && (
          <div className="mt-5 mb-2 group-data-[collapsible=icon]:hidden">
            <div className="px-3 text-sm font-semibold text-white/90">My Obyects:</div>
            <Separator className="mt-2 bg-white/10" />
          </div>
          )}

          {/* FAVORITES + COLLECTIONS - Hide on admin routes */}
          {!isAdminRoute && (
          <SidebarGroup>
            <SidebarGroupContent className="space-y-3 group-data-[collapsible=icon]:space-y-1">
              {/* FAVORITES — uses GradientHeart on hover & active */}
              <div className={cn(
                "bg-[#13171c]/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/5 px-3 py-2 transition-all duration-200",
                "group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:backdrop-blur-none group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-1 group-data-[collapsible=icon]:rounded-none"
              )}>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Favorites">
                      <NavLink
                        to="/favorites"
                        end
                        onClick={handleNavClick("/favorites")}
                        className="group flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-150 font-bold hover:bg-white/5"
                      >

                        {({ isActive }) => (
                          <>
                            <div className="flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200">
                              {/* Active or Hover → GradientHeart / else → Normal Heart */}
                              <span className="transition-transform duration-150 group-hover:scale-110">
                                {isActive ? (
                                  <GradientHeart className="h-4 w-4" />
                                ) : (
                                  <Heart className="h-4 w-4 text-[#70BAFF] group-hover:hidden" />
                                )}
                                {/* show GradientHeart on hover if not active */}
                                {!isActive && (
                                  <span className="hidden group-hover:inline-block">
                                    <GradientHeart className="h-4 w-4" />
                                  </span>
                                )}
                              </span>
                            </div>
                            <span className="text-[15px] text-white/90 [[data-collapsible=icon]_&]:hidden">Favorites</span>
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </div>

              {/* COLLECTIONS (Upgrade button moved INSIDE this card) */}
              <div className="bg-[#13171c]/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/5 px-3 py-2 transition-all duration-200 group-data-[collapsible=icon]:hidden">
                <Collapsible open={collectionsOpen} onOpenChange={setCollectionsOpen}>
                  <div className="flex items-center w-full gap-2">
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center gap-2 flex-1 px-2 py-1 rounded-md hover:bg-white/5 transition-all font-semibold text-white/90">
                        <ChevronRight
                          className={cn('h-4 w-4 transition-transform', collectionsOpen && 'rotate-90')}
                        />
                        <span className="text-[15px]">Collections</span>
                      </button>
                    </CollapsibleTrigger>

                    {/* + button – create collection inside sidebar */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollectionsOpen(true);
                        setShowInlineCreate(true);
                        setNewCollectionName(""); // 👈 reset
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all ml-auto"
                      aria-label="New collection"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>


                  <CollapsibleContent className="mt-1 pl-3 space-y-1">
                  {/* Search input */}
                  {isAuthenticated && !loading && visibleCollections.length > 0 && (
                    <div className="px-2 py-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search collections..."
                          className="h-9 pl-9 text-sm bg-black/40 border-white/10 focus-visible:ring-white/30 text-white placeholder-white/40"
                        />
                      </div>
                    </div>
                  )}

                  {/* Inline create at top when user clicks + */}
                  {showInlineCreate && !loading && (
                  <form
                    className="px-2 py-1 flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleCreateSidebarCollection(newCollectionName);
                    }}
                  >
                    <Input
                      autoFocus
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      placeholder="New collection name"
                      className="h-9 text-sm bg-black/40 border-white/10 focus-visible:ring-white/30 flex-1 text-white placeholder-white/40"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      className="h-8 w-8 rounded-full text-xs"
                      disabled={!newCollectionName.trim()}
                    >
                      ✓
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full text-xs"
                      onClick={() => {
                        setShowInlineCreate(false);
                        setNewCollectionName("");
                      }}
                    >
                      ✕
                    </Button>
                  </form>
                )}


                  {!isAuthenticated ? (
                    <div className="px-2 py-3 text-center">
                      <p className="text-sm text-white/60 mb-2">Login to create collections</p>
                      <Button 
                        asChild 
                        size="sm" 
                        className="w-full bg-[#70BAFF]/20 hover:bg-[#70BAFF]/30 text-[#70BAFF] border border-[#70BAFF]/30"
                      >
                        <Link to="/auth/signin">Login</Link>
                      </Button>
                    </div>
                  ) : loading ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground z-75">Loading...</div>
                  ) : visibleCollections.length === 0 ? (
                    <div className="px-2 py-3 text-center">
                      <p className="text-sm text-white/60">No collections yet</p>
                    </div>
                  ) : filteredCollections.length === 0 ? (
                    <div className="px-2 py-3 text-center">
                      <p className="text-sm text-white/60">No collections found</p>
                    </div>
                  ) : (
                    filteredCollections.map((collection: any) => (
                    <div
                      key={collection.id}
                      className="group flex items-center px-2 py-1.5 rounded-md transition-all duration-150 font-bold hover:bg-white/5"
                    >
                      {/* LEFT: link area */}
                     <NavLink
                        to={`/collection/${collection.id}`}
                        onClick={handleNavClick(`/collection/${collection.id}`)}
                        className="flex items-center gap-2 flex-1 min-w-0"
                      >

                        {({ isActive }) => (
                          <>
                            <div
                              className={cn(
                                "flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200",
                                isActive ? "bg-black" : "bg-transparent group-hover:bg-black/5"
                              )}
                            >
                              <img
                                src="/lovable-uploads/black-logo.png"
                                alt="Collection Icon"
                                className={cn(
                                  "h-4 w-4 object-contain transition-all",
                                  isActive ? "brightness-0 invert filter" : "opacity-70 group-hover:opacity-100 invert"
                                )}
                              />
                            </div>
                            <span className="text-sm text-white/90 truncate">{collection.name}</span>
                          </>
                        )}
                      </NavLink>

                      {/* RIGHT: ... menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation(); // don't trigger NavLink
                            }}
                            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 ml-1"
                            aria-label="Collection options"
                          >
                            <MoreHorizontal className="h-4 w-4 text-white/60" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          sideOffset={4}
                          className="z-[999] bg-popover shadow-lg border border-border"
                        >
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openRenameDialog(collection);
                            }}
                          >
                            Rename
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openDeleteDialog(collection);
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                  )}
                </CollapsibleContent>

                </Collapsible>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
          )}
        </SidebarContent>
        

        {/* FOOTER */}
        <SidebarFooter className="mt-auto p-0 border-t border-white/10">
        {/* 🔶 Upgrade Plan — standalone card above footer */}
          {entitlementLoaded && isAuthenticated && !isSubscribed && (
            <div className={cn(
              "bg-[#13171c]/80 backdrop-blur-xl m-2 rounded-2xl shadow-lg border border-white/5 px-3 py-2 transition-all duration-200",
              "group-data-[collapsible=icon]:m-0 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-1 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:backdrop-blur-none group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:rounded-none"
            )}>
              <button
                onClick={openPricingModal}
                className={cn(
                  "btn-glow w-full text-sm font-medium text-white rounded-xl opacity-100",
                  collapsed ? "px-2 py-1.5" : "px-4 py-2"
                )}
                style={{
                  backdropFilter: 'blur(4px)',
                  background: 'linear-gradient(to bottom, #70BAFF, #258BFF)'
                  // `radial-gradient(at bottom left, #e2466c, transparent 80%),
                  //   radial-gradient(at bottom right, #d088ff, transparent 70%),
                  //   radial-gradient(at top left, #765be6, transparent 85%),
                  //   radial-gradient(at top right, #bcbdda, transparent 70%)`,
                }}
              >
                Upgrade Plan
              </button>
            </div>
          )}
          <div className="px-3 py-3 border-t border-white/10 rounded-none text-white/70">
            <SidebarMenu className={cn(collapsed ? "space-y-0.5" : "space-y-1")}>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Request a feature">
                  <a
                    href="https://tally.so/r/3jKaNE"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg hover:bg-black/5 transition-colors flex items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="text-sm group-data-[collapsible=icon]:hidden">Request a feature</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Contact us">
                  <a
                    href="https://tally.so/r/wvk08d"
                    className="rounded-lg hover:bg-black/5 transition-colors flex items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-2"
                  >
                    <Mail className="h-4 w-4" />
                    <span className="text-sm group-data-[collapsible=icon]:hidden">Contact us</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Discover Subyect Agency">
                  <a
                    href="https://www.subyect.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg hover:bg-black/5 transition-colors flex items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-2"
                  >
                    <img
                      src={SUBYECT_LOGO}
                      alt="Subyect"
                      width={16}
                      height={16}
                      className="h-4 w-4 rounded-[3px] object-contain"
                    />
                    <span className="text-sm group-data-[collapsible=icon]:hidden">Discover Subyect Agency</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>

          {/* Footer user area: skeleton → login → profile */}
          {userInfo === undefined ? (
            // Loading skeleton (prevents flicker)
              <div
                className={cn(
                  "mx-3 my-2 rounded-2xl bg-white/5 backdrop-blur overflow-hidden",
                  collapsed ? "p-2 h-10" : "px-3 py-3"
                )}
              >
                <div className={cn("animate-pulse flex items-center", collapsed ? "justify-center" : "gap-3")}>
                  <div className="h-8 w-8 rounded-md bg-white/10" />
                  {!collapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="h-3 w-28 bg-white/10 rounded mb-2" />
                      <div className="h-2.5 w-40 bg-white/10 rounded" />
                    </div>
                  )}
                </div>
            </div>
          ) : userInfo === null ? (
            // Guest → polished Login tile (matches glassy profile card)
            <Button asChild variant="outline" className="bg-black text-white backdrop-blur-xl rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.05)] px-3 py-2 transition-all duration-200 m-2 mb-6">
                <Link to="/auth/signin">Login</Link>
            </Button>
          ) : (
            // Logged-in → profile card (your original look)
            <a
              href="/profile"
              className={cn(
                "bg-[#13171c]/80 backdrop-blur-xl shadow-lg border border-white/5 hover:bg-[#13171c]/90",
                // Collapsed: minimal, just centered avatar
                "group-data-[collapsible=icon]:m-0 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:backdrop-blur-none group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:rounded-none",
                collapsed ? "p-2 flex items-center justify-center" : "px-3 py-2 flex items-center gap-3"
              )}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={userInfo?.avatar || undefined} />
                <AvatarFallback className="bg-[#70BAFF]/10 text-[#70BAFF] font-semibold">
                  {(userInfo?.name?.[0] || "U").toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-white/90">{userInfo?.name || "Profile"}</p>
                  <p className="text-xs text-white/50 truncate">{userInfo?.email || ""}</p>
                </div>
              )}
            </a>
          )}

            {/* ✅ Rename Collection Dialog */}
            <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
              <DialogContent className="max-w-sm bg-white/80 backdrop-blur-xl border border-black/10 shadow-[0_18px_45px_rgba(15,23,42,0.18)]">
                <DialogHeader>
                  <DialogTitle className="text-sm font-semibold text-black">
                    Rename collection
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    Give your collection a clear, memorable name. This won’t affect your saved ads.
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-3 space-y-2">
                  <label className="text-xs font-medium text-black/80">Collection name</label>
                  <Input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    placeholder="e.g. UGC Hooks • Q4"
                    className="h-9 text-sm bg-white/80 border-black/10 focus-visible:ring-black/30"
                  />
                </div>

                <DialogFooter className="mt-4 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-black/10 text-xs"
                    onClick={() => {
                      setRenameDialogOpen(false);
                      setActiveCollection(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs px-3"
                    onClick={handleConfirmRename}
                    disabled={!renameValue.trim()}
                  >
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* ✅ Delete Collection Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent className="max-w-sm bg-white/85 backdrop-blur-xl border border-red-100 shadow-[0_18px_45px_rgba(127,29,29,0.18)]">
                <DialogHeader>
                  <DialogTitle className="text-sm font-semibold text-red-700">
                    Delete collection?
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    This will remove{" "}
                    <span className="font-semibold text-black">
                      {activeCollection?.name || "this collection"}
                    </span>{" "}
                    from your sidebar. Your ads will still be available in the Library.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-black/10 text-xs"
                    onClick={() => {
                      setDeleteDialogOpen(false);
                      setActiveCollection(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="text-xs px-3"
                    onClick={handleConfirmDelete}
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog> 
        </SidebarFooter>
      </div>
      <SidebarRail />
    </Sidebar>
  );
}
